/**
 * Billing (C06 epic): audit orders and receipts, then invoices. Amounts in minor units, formatted AED/ar-AE (QM-D08-003).
 * Phase 1 (G13): Quixo Audit orders come first (GET /v1/growth-audit/orders: orders placed in the portal plus website
 * orders linked to this workspace). The CRM subscription and plan picker show unless the workspace is audit-only.
 */
import { Badge, DataTable, EmptyState, ErrorState, Heading, Loading, Panel, Stat, Text, type Column } from '@qm/ui';
import { useFullOs } from '../../../lib/flags';
import { subjectLabel, tierKey, type AuditOrder } from '../audit/audit-view';
import { useApp } from '../../../lib/app-context';
import { BffError } from '../../../lib/bff';
import { useFormat } from '../../../lib/format';
import { useResource } from '../../../lib/resource';
import { ListScreen, str } from '../_shared/ListScreen';
import { PlanPicker } from './PlanPicker';

interface Subscription { plan?: string; planCode?: string; status?: string; amountMinor?: number; currency?: string; currentPeriodEnd?: string }

export function BillingPage() {
  const { t } = useApp();
  const f = useFormat();
  const os = useFullOs();
  const sub = useResource<Subscription | null>(os ? '/v1/billing/subscription' : null);
  const orders = useResource<AuditOrder[]>('/v1/growth-audit/orders');
  let summary;
  const noPlan = (sub.status === 'error' && sub.error instanceof BffError && sub.error.status === 404) || (sub.status === 'ready' && !sub.data);
  if (sub.status === 'loading') summary = <Loading label={t('common.loading')} />;
  else if (sub.status === 'error') summary = sub.error instanceof BffError && sub.error.status === 404 ? <EmptyState title={t('shell.screens.billing.noSubscription')} /> : <ErrorState message={t('shell.error.load')} />;
  else if (!sub.data) summary = <EmptyState title={t('shell.screens.billing.noSubscription')} />;
  else summary = (
    <Panel>
      <Stat label={t('shell.screens.billing.plan', { plan: str(sub.data.plan ?? sub.data.planCode) })} value={f.money(sub.data.amountMinor ?? 0, sub.data.currency)} />
      <Text tone="muted">{t('shell.screens.billing.renews', { when: f.date(sub.data.currentPeriodEnd) })}</Text>
    </Panel>
  );
  return (
    <ListScreen titleKey="shell.screens.billing.title" introKey="shell.screens.billing.intro" path="/v1/billing/invoices" emptyKey="shell.screens.billing.empty"
      columns={[
        { key: 'number', header: t('shell.col.invoice'), render: (r) => <bdi>{str(r.number ?? r.id)}</bdi> },
        { key: 'issuedAt', header: t('shell.col.date'), render: (r) => f.date(str(r.issuedAt ?? r.createdAt)) },
        { key: 'total', header: t('shell.col.amount'), numeric: true, render: (r) => f.money(Number(r.totalMinor ?? r.amountMinor ?? 0), str(r.currency) || undefined) },
        { key: 'status', header: t('shell.col.status'), render: (r) => str(r.status) },
      ]}>
      <Heading level={2}>{t('shell.p1.billing.orders')}</Heading>
      <AuditOrders res={orders} />
      {os ? (
        <>
          <Heading level={2}>{t('shell.screens.billing.subscription')}</Heading>
          {summary}
          {noPlan ? <PlanPicker /> : null}
        </>
      ) : null}
      <Heading level={2}>{t('shell.screens.billing.invoices')}</Heading>
    </ListScreen>
  );
}

const ORDER_TONE: Record<AuditOrder['status'], 'success' | 'accent' | 'warning' | 'error' | 'neutral'> = {
  paid: 'success', free: 'accent', awaiting_payment: 'neutral', payment_failed: 'error', expired: 'neutral', refunded: 'warning',
};

function AuditOrders({ res }: { res: ReturnType<typeof useResource<AuditOrder[]>> }) {
  const { t } = useApp();
  const f = useFormat();
  if (res.status === 'loading') return <Loading label={t('common.loading')} />;
  if (res.status === 'error') return res.error instanceof BffError && res.error.status === 403 ? <Text tone="muted">{t('shell.screens.auditDash.ordersRole')}</Text> : <ErrorState message={t('shell.error.load')} />;
  if (!res.data.length) return <EmptyState title={t('shell.screens.auditDash.noOrders')} />;
  return (
    <>
      <DataTable caption={t('shell.p1.billing.orders')} columns={[
        { key: 'date', header: t('shell.col.date'), render: (r) => f.date(r.paidAt ?? r.createdAt) },
        { key: 'tier', header: t('shell.screens.auditDash.tierCol'), render: (r) => { const k = tierKey(r.tier ?? r.sku); return k ? t(`shell.p1.tier.${k}`) : ''; } },
        { key: 'subject', header: t('shell.p1.col.subject'), render: (r) => (r.website ? <bdi>{f.text(subjectLabel(r.website).label)}</bdi> : '') },
        { key: 'amount', header: t('shell.col.amount'), numeric: true, render: (r) => (r.status === 'free' ? t('shell.p1.billing.free') : f.money(r.amountMinor, r.currency)) },
        { key: 'status', header: t('shell.col.status'), render: (r) => <Badge tone={ORDER_TONE[r.status]}>{t(`shell.p1.billing.status.${r.status}`)}</Badge> },
      ] satisfies Column<AuditOrder>[]} rows={res.data} />
      <Text tone="muted">{t('shell.p1.billing.receipts')}</Text>
    </>
  );
}
